import _ from 'lodash';
import React, { ReactNode, RefObject } from 'react';
import { createRoot } from 'react-dom/client';
import { connect, ConnectedProps } from 'react-redux';
import produce from 'immer';
import cn from 'classnames';
import { BBCommonProps, BBEditableModeProps, StoryModel } from 'types/story';
import { COMPONENT_STATES, COMPONENT_TYPE, HAS_ANSWERS_ATTR } from 'common/constants';
import { location } from 'common/utils/url-helper';
import { sleep } from 'common/utils/helpers';
import { ClientReducerState } from 'client/reducers';
import { CardFacade } from 'common/utils/facades/card-facade';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { StoryHistory } from 'client/utils';
import { sendAnswer, getCardVotes } from 'client/actions/api';
import { TimerEvEm } from 'client/components/common/BuildingBlocks/Timer/utils';
import { updateAnswers } from 'client/actions/update-answers';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { ScriptRenderer } from 'client/components/common/ScriptRenderer/ScriptRenderer';
import { EventProviderContextT, withEventProvider } from 'client/components/pages/Story/EventProvider/Context';
import AnswerContext from './Context';
import css from './Answer.scss';

const { isPreview } = location.client;
const PREVIEW_VOTES: Record<string, Record<string, number>> = {};
const WRONG_ANSWER_DELAY = 500;
const extraAttrs = { [HAS_ANSWERS_ATTR]: false };

function getIsCorrect(id: Props['_id'], answersSettings: Props['answersSettings']) {
	const answerSettings = answersSettings?.[id];
	return answerSettings && 'isCorrect' in answerSettings ? answerSettings.isCorrect! : false;
}

function getResult(id: Props['_id'], answersSettings: Props['answersSettings']) {
	const isCorrect = getIsCorrect(id, answersSettings);
	return { correct: isCorrect, incorrect: !isCorrect };
}

function setPreviewVotes(answerId: string, cardId: string) {
	if (PREVIEW_VOTES[cardId][answerId] !== -1) {
		return;
	}

	const votesAll = Object.values(PREVIEW_VOTES[cardId]);
	const votes = votesAll.filter(v => v !== -1);
	const isLast = votesAll.length - votes.length === 1;
	const total = _.sum(votes);
	const rest = 100 - total;

	PREVIEW_VOTES[cardId][answerId] = isLast ? rest : _.random(0, rest);
}

const memo = {};

const connector = connect(
	({ card, story, user }: ClientReducerState, ownProps: BBCommonProps) => {
		if (!memo[ownProps._id]) {
			// why? - to avoid update component with next card from state
			memo[ownProps._id] = card;
		}

		const cardData = memo[ownProps._id] || card;
		const SF = new StoryFacade(story as StoryModel);
		const CF = new CardFacade(cardData);
		const { cardId } = CF;
		const showResults = CF.getShowResults(SF.settings);
		const showCorrect = CF.getShowCorrect(SF.settings);
		const settings = SF.settings.cards?.[cardId];

		return {
			storyId: SF.storyId,
			cardId,
			showResults,
			answersSettings: settings?.answers,
			showCorrect,
			answersData: produce(user.answers[cardId], draft => {
				// Provide fake votes result to props
				if (isPreview && showResults) {
					if (_.isUndefined(_.get(PREVIEW_VOTES, [cardId, ownProps._id]))) {
						_.set(PREVIEW_VOTES, [cardId, ownProps._id], -1);
					}

					if (draft && draft.selected.length > 0) {
						setPreviewVotes(ownProps._id, cardId);

						_.set(draft, 'votes', { ...PREVIEW_VOTES[cardId] });
					}
				}
			}),

			// NOTE: multiple answers is not implemented yet, added just for a future purposes
			answersMax: settings?.answersMax ?? 1,
		};
	},
	{
		sendAnswer,
		updateAnswers,
		getCardVotes,
	}
);

type Props = ConnectedProps<typeof connector> & BBCommonProps & EventProviderContextT & { children: ReactNode };

type State = { result: null | { correct: boolean; incorrect: boolean } };

/**
 * How does it work:
 * 1. Click on Answer
 * 2. Send answer (only prod)
 * 3. Get votes statistics from BE (only prod & showResults & hasResultBlocks)
 * 4. Store answer & votes in client redux store
 * 4.1. re-render component and provide answerData
 * 4.2. @:getDerivedStateFromProps: add answer result to state of selected Answer
 * 4.2.1. @:componentDidUpdate: (only if "isCanBeCorrect": true) update correct|incorrect state and show it visually
 * 4.3. (only if "showResults" & "hasResultBlocks") render children with votes results from 4.1 (ResultText|ResultShape)
 */
class Answer extends React.Component<Props, State> {
	state = {
		result: null,
	};

	/**
	 * User can delete result blocks in Answer and in this case showResults must a false.
	 */
	hasResultBlocks?: boolean = false;

	// if true: prevent send answer multiple times
	isClicked = false;

	/* prevent infinite componentDidUpdate call by updateCorrectStates()
	   in case when Answer is by fault is nested in Swipe */
	isUpdatedCorrectStates = false;

	static getDerivedStateFromProps(props: Props, state: State): Partial<State> {
		const nextState: Partial<State> = {};

		const isCurrentAnswered = props.answersData?.selected?.includes(props._id);
		if (isCurrentAnswered) {
			nextState.result = getResult(props._id, props.answersSettings);
		}

		return nextState;
	}

	componentDidMount() {
		const { onClickCapture } = this.ownListeners;
		// Register component events in provider, potentially Swipe BB can use it.
		if (onClickCapture)
			this.props.eventProvider.setComponentEvents(this.props._id, { onAnswerClick: onClickCapture });
	}

	componentDidUpdate(prevProps: Props, prevState: State) {
		if (this.props.showCorrect) {
			this.showAnswerResultByCorrectProp();
		}
	}

	get selected() {
		return this.props.answersData?.selected ?? [];
	}

	get isAllowAnswer() {
		let isAllow = this.selected.length === 0;

		if (this.props.answersMax > 1) {
			isAllow = this.selected.length < this.props.answersMax;
		}

		return isAllow;
	}

	// return timeout in milliseconds
	get answerTimeout() {
		const at = parseFloat(this.props.uiConfig.componentProps.answerTimeout as string);
		const min = this.showResults && Number.isNaN(at) ? 1 : 0;
		return Math.max(min, Number.isNaN(at) ? min : at) * 1000;
	}

	get ownListeners() {
		if (this.props.isEditableMode) {
			return {};
		}

		return {
			/**
			 * @info Capture is used to prevent any events in child nodes. Nothing should interfere an Answer logic
			 * @param event isn't always a SyntheticEvent, because of listener may be invoked programmatically
			 *        by another component. Event can be equal to "swipe" only when it is called by Swipe BB
			 */
			onClickCapture: async (event: React.MouseEvent<HTMLElement, MouseEvent> | 'swipe') => {
				const isMouseEvent = event !== 'swipe' && event?.nativeEvent instanceof Event;

				if (isMouseEvent) {
					event.stopPropagation(); // prevent nested dom nodes event handlers
					event.preventDefault(); // prevent click on nested <a> nodes

					if (this.isClicked || !this.isAllowAnswer) {
						/*
						  Prevent Answer click in case of:
						  - event is not called by Swipe component through eventProvider
						  - mouse event is called more than 1 time
						  - reached answersMax
						  */
						return;
					}
					// The problem is when you do event.stopPropagation in capture phase it cancels handler in
					// bubbling phase on the element, what breaks animation with onclick trigger,
					// so as fix — normal onclick listener is called manualy:
					this.props.eventListeners?.onClick?.(event);
				}

				this.isClicked = true;

				TimerEvEm.emit('pause', this.props.cardId);

				const nextStates = { [COMPONENT_STATES.HOVER]: false };

				if (!this.props.showCorrect) nextStates[COMPONENT_STATES.SELECTED] = true;

				this.props.setStates(nextStates);

				// Call event assigned by Swipe BB (optional. example is a "TrueOrFalse|ThisOrThat" card)
				const providedSwipeHandler = this.props.eventProvider.events.component[this.props._id]?.onSwipe;
				if (isMouseEvent && providedSwipeHandler) {
					providedSwipeHandler();
					return;
				}

				const { showResults } = this;
				const { cardId, storyId, _id } = this.props;

				const sendAnswerPayload = {
					storyId,
					cardId,
					answerId: _id,
					answerText: this.props.containerRef?.current?.textContent || 'MediaContent*',
				};

				let votes: { [key: string]: number } = {};
				try {
					if (showResults && !isPreview) {
						// Store answer in BE
						this.props.sendAnswer(sendAnswerPayload);
						// Get votes distribution
						const cardVotes = await this.props.getCardVotes({ cardId, storyId });
						if (cardVotes) votes = cardVotes;
					} else if (!isPreview) {
						// Store answer in BE
						this.props.sendAnswer(sendAnswerPayload);
					}
				} catch (e) {
					console.error(e);
				}

				// Store answer in redux
				this.props.updateAnswers({
					cardId: this.props.cardId,
					answerId: _id,
					votes,
				});

				// Store answer to localStorage (used then to calculate a total score)
				const sHistory = new StoryHistory({ storyId });
				sHistory.addAnswer({ cardId, answerId: _id });

				// Render answer embed code
				const pixels = this.props.uiConfig.componentProps?.other?.embedCode;
				if (pixels) {
					const container = document.createElement('div');
					container.id = `answer-script-${cardId}-${_id}`;
					document.body.appendChild(container);
					const root = createRoot(container);
					root.render(<ScriptRenderer value={pixels} scriptOnly />);
				}

				if (this.props.showCorrect && !getIsCorrect(this.props._id, this.props.answersSettings)) {
					// wait WRONG_ANSWER_DELAY to show correct and wait 500 before to go forward
					await sleep(WRONG_ANSWER_DELAY + 500);
				} else if (nextStates[COMPONENT_STATES.SELECTED]) {
					await sleep(500);
				}

				if (this.answerTimeout) await sleep(this.answerTimeout);

				this.props.eventProvider.events.flow(_id);
			},
		};
	}

	get showResults() {
		return this.props.showResults && this.hasResultBlocks;
	}

	/**
	 * Update answer components states, to show visually is answer correct or not.
	 */
	showAnswerResultByCorrectProp = async () => {
		const p: Props = this.props;
		const s: State = this.state;
		const { CORRECT, INCORRECT } = COMPONENT_STATES;

		/**
		 * Update correct|incorrect states at "this.props.states" & "this.props.statesAttrs"
		 */
		const updateCorrectStates = () => {
			if (!s.result) {
				return;
			}

			const { correct, incorrect } = s.result;

			if (correct !== p.states[CORRECT] || incorrect !== p.states[INCORRECT]) {
				this.isUpdatedCorrectStates = true;
				p.setStates({
					...p.states,
					[CORRECT]: correct,
					[INCORRECT]: incorrect,
				});
			}
		};

		if (!this.isUpdatedCorrectStates) {
			updateCorrectStates();
		}

		const forceShowCorrect =
			p.showCorrect &&
			!s.result &&
			this.selected.length === p.answersMax &&
			getIsCorrect(this.props._id, this.props.answersSettings);
		if (forceShowCorrect) {
			await sleep(WRONG_ANSWER_DELAY);
			this.setState({ result: getResult(p._id, p.answersSettings) });
		}
	};

	mapChildren = () =>
		React.Children.map(this.props.children, child => {
			if (!React.isValidElement<BBCommonProps>(child)) {
				return child;
			}

			const nextProps: Partial<BBCommonProps> = {
				...child.props,
				parentStates: this.props.states, // change child states to answer state
			};

			if (child.props.type === COMPONENT_TYPE.RESULT_TEXT || child.props.type === COMPONENT_TYPE.RESULT_SHAPE) {
				if (this.props.showResults && _.get(child.props, 'uiConfig.componentProps.styles.display') !== 'none') {
					this.hasResultBlocks = true;
				}

				const isCorrect = this.props.states[COMPONENT_STATES.CORRECT];
				const isShow = !this.props.isEditableMode && this.selected.length === this.props.answersMax;
				const noVotes = _.isEmpty(this.props.answersData?.votes);
				let value = this.props.answersData?.votes?.[this.props._id];

				if (value === undefined) {
					value = isCorrect && noVotes ? 100 : 0;
				}

				// todo: pass via context
				// Provide vote data to child components to display vote results (see ResultText, ResultShape)
				nextProps.answerVote = {
					isShow,
					value,
					duration: Math.max(350, this.answerTimeout * 0.35),
				};
			}

			return React.cloneElement<BBCommonProps>(child, nextProps);
		});

	renderEditable() {
		const props = { ...this.props.editableModeProps?.nodeProps };
		return this.renderDefault(props);
	}

	renderDefault(props: Partial<BBEditableModeProps['nodeProps']> = {}) {
		const { nodeProps } = this.props.uiConfig;

		extraAttrs[HAS_ANSWERS_ATTR] = !!this.selected.length;

		return (
			<div
				{...this.props.uiConfig.nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...this.ownListeners}
				{...props}
				{...extraAttrs}
				className={cn(css.answer, nodeProps.className, props.className)}
				ref={this.props.containerRef as RefObject<HTMLDivElement>}
			>
				<AnswerContext.Provider value={{ showResults: this.props.showResults }}>
					{this.props.isEditableMode ? this.props.children : this.mapChildren()}
				</AnswerContext.Provider>
			</div>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

const WithRedux = connector(Answer);
const WithCardTransitionContext = withCardTransitionContext(WithRedux);

export default withEventProvider(WithCardTransitionContext);
